import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getProgramScopeWithSnapshots } from "../../../utils/api/endpoints/programs/scopeHierarchical";
import { IoMdSearch } from "react-icons/io";
import { HiChevronDown, HiChevronRight, HiPlus, HiShieldCheck } from "react-icons/hi";
import Pagination from "../../../components/retests/utils/Pagination";
import useUserCredentials from "../../../utils/hooks/user/useUserCredentials";
import { UserRole } from "../../../utils/api/endpoints/user/credentials";
import AddEndpointModal from "./AddEndpointModal";
import useProgram from "../../../utils/hooks/programs/useProgram";

const ScopeTable: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { role } = useUserCredentials();
  const { program } = useProgram(id ? parseInt(id, 10) : undefined);
  const isV1 = program?.architecture_version === 1;

  const [hierarchicalData, setHierarchicalData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedTargets, setExpandedTargets] = useState<Record<string, boolean>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  // Add Endpoint Modal state
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedTargetForAdd, setSelectedTargetForAdd] = useState<any>(null);

  const fetchData = async () => {
    if (!id || isV1) return;
    setLoading(true);

    try {
      const response = await getProgramScopeWithSnapshots(Number(id));
      if (response?.data) {
        setHierarchicalData(response.data);
      }
    } catch (error) {
      console.error("Error fetching hierarchical scope:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isV1) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [id, isV1]);

  const toggleTarget = (targetId: string) => {
    setExpandedTargets(prev => ({
      ...prev,
      [targetId]: !prev[targetId]
    }));
  };

  const handleAddEndpoint = (target: any) => {
    setSelectedTargetForAdd(target);
    setIsAddModalOpen(true);
  };

  const isResearcher = role === UserRole.RESEARCHER;
  
  // Use program.targets for V1, otherwise use hierarchical data
  const targets = isV1 
    ? (program?.targets || []).map((t, idx) => ({
        target_id: `v1-${idx}`,
        target_name: t.targetName,
        target_type: t.targetType,
        target_value: t.targetName, // In V1, targetName is usually the value
        resources: [] // V1 doesn't have hierarchical resources in this view
      }))
    : (hierarchicalData?.targets || []);

  const filteredTargets = targets.filter((target: any) =>
    (target.target_name || target.name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
    (target.target_value || target.value || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalPages = Math.ceil(filteredTargets.length / itemsPerPage);
  const currentTargets = filteredTargets.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
 
  return (
    <div className="space-y-4">
      {/* Asset Header Info - Hide for V1 or show Program Info */}
      {isV1 ? (
        <div className="bg-white rounded-lg shadow-sm border p-4 mb-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center text-[#010D2C]">
              <HiShieldCheck className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-[#010D2C]">{program?.title || "Legacy Program"}</h2>
              <div className="flex gap-2 text-xs">
                <span className="text-gray-500 uppercase font-medium">Legacy Scope</span>
              </div>
            </div>
          </div>
          <div className="text-right">
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold uppercase bg-blue-100 text-blue-700 border border-blue-200">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              Legacy Data
            </span>
          </div>
        </div>
      ) : hierarchicalData?.asset && (
        <div className="bg-white rounded-lg shadow-sm border p-4 mb-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center text-[#010D2C]">
              <HiShieldCheck className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-[#010D2C]">{hierarchicalData.asset.asset_name || hierarchicalData.asset.name}</h2>
              <div className="flex gap-2 text-xs">
                <span className="text-gray-500 uppercase font-medium">{hierarchicalData.asset.asset_type || hierarchicalData.asset.type}</span>
                <span className="text-gray-300">|</span>
                <span className={`font-semibold ${hierarchicalData.asset.criticality === 'CRITICAL' ? 'text-red-500' :
                  hierarchicalData.asset.criticality === 'HIGH' ? 'text-orange-500' : 'text-blue-500'
                  }`}>{hierarchicalData.asset.criticality}</span>
              </div>
            </div>
          </div>
          <div className="text-right flex flex-col gap-2">
            <div className="text-xs text-gray-500">Primary Asset for this Program</div>
            {hierarchicalData.hasSnapshots !== undefined && (
              <div className="flex justify-end">
                {hierarchicalData.hasSnapshots ? (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold uppercase bg-purple-100 text-purple-700 border border-purple-200">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                      <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd" />
                    </svg>
                    Snapshot Data ({hierarchicalData.snapshotCount} {hierarchicalData.snapshotCount === 1 ? 'Run' : 'Runs'})
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold uppercase bg-blue-100 text-blue-700 border border-blue-200">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    Live Data
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Targets and Search */}
      <div className="bg-white rounded-lg shadow-sm border p-4">
        <div className="flex items-center gap-4 mb-4 relative">
          <div className="relative flex-1">
            <IoMdSearch className="absolute top-1/2 left-3 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search targets or values..."
              className="border p-2 pl-10 rounded-lg w-full outline-none focus:ring-2 focus:ring-blue-500/20"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <div className="overflow-hidden rounded-lg border border-gray-100">
          <table className="w-full border-collapse">
            <thead className="bg-[#010D2C] text-white">
              <tr>
                <th className="px-6 py-4 text-left font-medium w-10"></th>
                <th className="px-6 py-4 text-left font-medium">Target Name</th>
                <th className="px-6 py-4 text-left font-medium">Type</th>
                <th className="px-6 py-4 text-left font-medium">Value</th>
                <th className="px-6 py-4 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="text-center py-8 text-gray-500">
                    <div className="flex flex-col items-center gap-2">
                      <svg className="animate-spin h-5 w-5 text-blue-500" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      <span>Loading program scope...</span>
                    </div>
                  </td>
                </tr>
              ) : currentTargets.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-8 text-gray-500">
                    No targets found in scope
                  </td>
                </tr>
              ) : (
                currentTargets.map((target: any, index: number) => {
                  const targetId = target.target_id || `legacy-${index}`;
                  const isExpanded = expandedTargets[targetId];
                  const resources = target.resources || [];

                  return (
                    <React.Fragment key={targetId}>
                      <tr className={`border-b transition hover:bg-gray-50/50 ${isExpanded ? 'bg-blue-50/10' : ''}`}>
                        <td className="px-6 py-4 text-center">
                          {resources.length > 0 ? (
                            <button onClick={() => toggleTarget(targetId)} className="p-1 hover:bg-gray-200 rounded">
                              {isExpanded ? <HiChevronDown className="w-5 h-5" /> : <HiChevronRight className="w-5 h-5" />}
                            </button>
                          ) : (
                            <div className="w-5 h-5"></div>
                          )}
                        </td>
                        <td className="px-6 py-4 font-medium text-gray-900">{target.target_name || target.name}</td>
                        <td className="px-6 py-4">
                          <span className="px-2 py-1 rounded-md bg-gray-100 text-xs font-semibold text-gray-600">
                            {target.target_type || target.type}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-gray-600 font-mono text-sm">
                          <div className="flex items-center gap-2">
                            {(!(target.target_value === "Uploaded Binary" || target.target_value === target.metadata?.binary_name) || !target.metadata?.binary_url) && (
                              <span>{target.target_value || target.value}</span>
                            )}
                            {/* Mobile Binary Download Link */}
                            {(target.metadata?.binary_name && target.metadata?.binary_url) && (
                              <a
                                href={target.metadata.binary_url}
                                download={target.metadata.binary_name}
                                className="flex items-center gap-1 px-2 py-1 text-xs font-bold text-blue-600 bg-blue-50 hover:bg-blue-100 rounded border border-blue-200 transition-colors"
                                title={`Download ${target.metadata.binary_name}`}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                </svg>
                                {target.metadata.binary_name}
                              </a>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right">
                          {isResearcher && (
                            <button
                              onClick={() => handleAddEndpoint(target)}
                              className="inline-flex items-center gap-1 text-xs font-bold text-blue-600 hover:text-blue-800"
                            >
                              <HiPlus className="w-4 h-4" />
                              ADD ENDPOINT
                            </button>
                          )}
                        </td>
                      </tr>
                      {isExpanded && resources.map((res: any) => (
                        <tr key={res.resource_id} className="bg-gray-50/50 border-b">
                          <td></td>
                          <td colSpan={4} className="px-6 py-3 pl-12">
                            <div className="flex items-center gap-3">
                              <div className="w-1.5 h-1.5 rounded-full bg-blue-400"></div>
                              <span className="text-sm font-mono text-gray-700">{res.value}</span>
                              <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-white border border-gray-200 text-gray-400 font-bold">
                                {res.resource_type}
                              </span>
                              {res.http_method && (
                                <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-blue-50 border border-blue-200 text-blue-600 font-bold">
                                  {res.http_method}
                                </span>
                              )}
                              {/* Show discovery badge if discovered by researcher */}
                              {res.discovery_method === 'PENTESTER' && res.creator && (
                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-50 border border-green-200 text-green-700 font-semibold flex items-center gap-1">
                                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" />
                                  </svg>
                                  Discovered by {res.creator.display_name || res.creator.username}
                                </span>
                              )}
                              {res.notes && (
                                <span className="text-xs text-gray-400 italic font-light truncate max-w-sm">
                                  - {res.notes}
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-4">
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
        </div>
      </div>

      {/* Add Endpoint Modal */}
      {hierarchicalData?.asset && selectedTargetForAdd && (
        <AddEndpointModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          onSuccess={fetchData}
          assetId={hierarchicalData.asset.id}
          targetId={selectedTargetForAdd.target_id}
          programId={Number(id)}
        />
      )}
    </div>
  );
};

export default ScopeTable;
